import React, { useEffect, useRef, useState } from "react";
import { message, Spin } from "antd";
import {
  CameraOutlined,
  RetweetOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import { convertBase64ToMiniFileAsync } from "@lhd/utility";
import { RiArrowGoBackFill } from "react-icons/ri";
import Button from "@lhd/components/Button";
import { Button as ButtonAntd } from "antd";
import { Image } from "antd";

import { BUTTONS_ACTION, LOG_TYPE } from "../contants";

interface IProps {
  handleOpenOrCloseCamera: (isOpen: boolean) => void;
  handleOkCamera: (files: File[], log_type: LOG_TYPE) => void;
  typeLogOpenCamera: LOG_TYPE;
}
const CameraCapture = (props: IProps) => {
  const { handleOkCamera, handleOpenOrCloseCamera, typeLogOpenCamera } = props;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [isMirrored, setIsMirrored] = useState(false);
  const [photos, setPhotos] = useState<
    { id: string; file: File; url: string }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [cameraInitDone, setCameraInitDone] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function initCamera() {
      try {
        const tempStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });

        if (cancelled) {
          tempStream.getTracks().forEach((t) => t.stop());
          return;
        }

        tempStream.getTracks().forEach((t) => t.stop());

        const devices = await navigator.mediaDevices.enumerateDevices();
        const cams = devices.filter((d) => d.kind === "videoinput");

        if (!cams.length) {
          if (!cancelled) message.error("カメラが見つかりません");
          return;
        }

        setVideoDevices(cams);
        setDeviceId(cams[0].deviceId);

        const label = (cams[0].label || "").toLowerCase();
        setIsMirrored(
          label.includes("front") ||
            label.includes("selfie") ||
            label.includes("前"),
        );
      } catch (e) {
        console.error(e);
        if (!cancelled) message.error("カメラの権限がありません");
      } finally {
        if (!cancelled) setCameraInitDone(true);
      }
    }

    initCamera();

    return () => {
      cancelled = true;
      stopCamera();
    };
  }, []);

  // =============================
  // Start camera
  // =============================
  useEffect(() => {
    if (!deviceId) return;
    startCamera();
    return stopCamera;
  }, [deviceId, photos]);

  async function startCamera() {
    stopCamera();

    try {
      setLoading(true);

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: deviceId } as any },
        audio: false,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (e) {
      console.error(e);
      message.error("カメラを起動できません");
    } finally {
      setLoading(false);
    }
  }

  function stopCamera() {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }

  // =============================
  // Capture
  // =============================
  async function takePhotos() {
    if (!videoRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current!;
    const w = video.videoWidth || 1280;
    const h = video.videoHeight || 720;

    canvas.width = w;
    canvas.height = h;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // mirror fix
    if (isMirrored) {
      ctx.translate(w, 0);
      ctx.scale(-1, 1);
    }

    ctx.drawImage(video, 0, 0, w, h);
    //setPhotos(canvas.toDataURL("image/png"));
    const base64 = canvas.toDataURL("image/png");
    const { file, blob } = await convertBase64ToMiniFileAsync(base64);
    setPhotos((prev) => [
      ...prev,
      { id: uuidv4(), file, url: URL.createObjectURL(blob) },
    ]);
  }

  function switchCamera() {
    if (videoDevices.length < 2 || !deviceId) return;

    const currentIndex = Math.max(
      0,
      videoDevices.findIndex((d) => d.deviceId === deviceId),
    );
    const nextIndex = (currentIndex + 1) % videoDevices.length;
    const next = videoDevices[nextIndex];

    setDeviceId(next.deviceId);
  }

  // =============================
  // Render
  // =============================

  return (
    <Spin spinning={loading || !cameraInitDone}>
      <div
        className="d-flex p-2 justify-end gap-2"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Button
          icon={<RiArrowGoBackFill size={16} />}
          className="back-btn"
          onClick={() => {
            handleOpenOrCloseCamera(false);
          }}
          label="戻る"
        />

        <Button
          onClick={() =>
            handleOkCamera(
              photos.map((p) => p.file),
              typeLogOpenCamera,
            )
          }
          label={
            BUTTONS_ACTION.find((x) => x.log_type === typeLogOpenCamera)
              ?.label || "出勤"
          }
        />
        {photos.length > 0 && (
          <Button
            onClick={() => {
              setPhotos([]);
            }}
            label="新しく写真を撮る"
          />
        )}
      </div>
      {cameraInitDone && !deviceId ? (
        <div style={{ textAlign: "center" }}>
          カメラへのアクセスが許可されていません。
        </div>
      ) : deviceId ? (
        <>
          {photos.length > 0 ? (
            photos.map((photo) => <Image key={photo.id} src={photo.url} />)
          ) : (
            <div>
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                style={{
                  width: "100%",
                  maxHeight: "calc(100dvh - 10px)",
                  borderRadius: 8,
                  transform: isMirrored ? "scaleX(-1)" : "none",
                  objectFit: "contain",
                }}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "end",
                  position: "absolute",
                  padding: 10,
                  bottom: 10,
                  width: "100%",
                }}
              >
                <div className="flex-1  ">
                  <ButtonAntd
                    icon={<RetweetOutlined />}
                    onClick={switchCamera}
                    disabled={videoDevices.length < 2 || loading}
                  ></ButtonAntd>
                </div>
                <div className="flex-1 d-flex justify-center">
                  <Button
                    type="primary"
                    icon={<CameraOutlined />}
                    onClick={takePhotos}
                    loading={loading}
                    label="撮影"
                    disabled={!deviceId}
                  />
                </div>
                <div className="flex-1 justify-end d-flex">
                  <ButtonAntd
                    icon={<SwapOutlined />}
                    onClick={() => setIsMirrored((v) => !v)}
                    disabled={loading}
                  ></ButtonAntd>
                </div>
              </div>
            </div>
          )}
          <canvas ref={canvasRef} style={{ display: "none" }} />
        </>
      ) : null}
    </Spin>
  );
};

export default CameraCapture;
